'use client';

import BoldIconSvg from '@/assets/svgs/text-editor/text-bold.svg';
import ItalicsIconSvg from '@/assets/svgs/text-editor/text-italic.svg';
import { Editor } from '@tiptap/react';
import classNames from 'classnames';
import { Fragment, useEffect, useRef } from 'react';
import { EditorStorageWithFormatting, TextEditorOperation, ToolbarItem } from '../types';
import AttributesDropdownButton from './AttributesDropdownButton';
import EmojiPickerButton from './EmojiPickerButton';
import FontSelectionDropdownButton from './FontSelectionDropdownButton';

import {
  selectIsShowingFloatingAttributes,
  selectIsShowingFloatingEmoji,
  selectIsShowingMediaManager,
  selectJobType,
} from '@/containers/Jobs/redux/jobEditors.selectors';
import { MediaItem } from '@/containers/MediaManager/types';
import { textEditorFonts } from '@/shared/utils/text-editor';
import { useSelector } from 'react-redux';
import FloatingAttributesDropdown from '../FloatingAttributesDropdown';
import FloatingEmojiDropdown from '../FloatingEmojiDropdown';
import useOperationsOnSelectionChange from '../hooks/useOperationsOnSelectionChange';
import useTextEditor from '../hooks/useTextEditor';
import useToolbarOperations from '../hooks/useToolbarOperations';
import InsertLinkDropdownButton from './InsertLinkDropdownButton';
import InsertMediaDropdownButton from './InsertMediaDropdownButton';
import RedoButton from './RedoButton';
import SimpleButton from './SimpleButton';
import UndoButton from './UndoButton';

interface TextEditorToolbarProps {
  className?: string;
  editor: Editor | null;
  tools: ToolbarItem[];
  isInlineToolbar?: boolean;
  jobId?: string;
  onMediaSelected?: (media: MediaItem) => void; // Custom callback for media selection
}

const TextEditorToolbar = ({
  className,
  isInlineToolbar,
  editor,
  tools,
  jobId = '',
  onMediaSelected,
}: TextEditorToolbarProps) => {
  const {
    isBold,
    isItalics,
    canUndo,
    canRedo,
    selectedFont,
    isItalicsSupported,
    isBoldSupported,
    isBoldButtonEnabled,
    isItalicsButtonEnabled,
    shouldDoOperation,
    setIsBold,
    setIsItalics,
    setShouldDoOperation,
    setCanUndo,
    setCanRedo,
    setSelectedFont,
    onToggleBoldOperation,
    onToggleItalicsOperation,
    onChangeFontOperation,
  } = useToolbarOperations();

  const jobType = useSelector(selectJobType(jobId));

  const containerRef = useRef<HTMLDivElement>(null);
  const isEmojiFloatingDropdownOpen = useSelector(selectIsShowingFloatingEmoji(jobId));
  const isAttributesFloatingDropdownOpen = useSelector(
    selectIsShowingFloatingAttributes(jobId)
  );
  const isMediaManagerDropdownOpen = useSelector(selectIsShowingMediaManager(jobId));

  const italicsSupportMessage = isItalicsSupported
    ? ''
    : 'Your selected font does not support Italics characters';

  const boldSupportMessage = isBoldSupported
    ? ''
    : 'Your selected font does not support Bold characters';
  const { applyFormattingToSelection, identifyOperationsFromText } =
    useTextEditor(editor);

  useOperationsOnSelectionChange({
    editor,
    shouldDoOperation,
    onSelectionChanged: (selectedHtml) => {
      // Don't reset formatting state if there's no actual selection (cursor is just positioned)
      // This preserves the bold/italic state when user clicks button then types
      if (!selectedHtml || selectedHtml.trim() === '') {
        return;
      }

      const {
        isBold: isSelectedTextBold,
        isItalics: isSelectedTextItalics,
        fontName,
      } = identifyOperationsFromText(selectedHtml);
      const font = textEditorFonts.find((f) => f.name === fontName);
      if (font) {
        setIsBold(isSelectedTextBold);
        setIsItalics(isSelectedTextItalics);
        setSelectedFont(font);
      }
    },
  });

  useEffect(() => {
    if (!editor) return;

    // Update state when the editor's content or selection changes
    const updateUndoRedoState = () => {
      setCanUndo(editor.can().undo());
      setCanRedo(editor.can().redo());
    };

    // Call the function initially and when the editor's state changes
    updateUndoRedoState();
    editor.on('transaction', updateUndoRedoState);

    // Cleanup event listener
    return () => {
      editor.off('transaction', updateUndoRedoState);
    };
  }, [editor, setCanRedo, setCanUndo]);

  // Store formatting state in editor storage for text input handling
  useEffect(() => {
    if (!editor) return;

    const storage = editor.storage as EditorStorageWithFormatting<typeof editor.storage>;
    if (!storage.formattingState) {
      storage.formattingState = {};
    }
    storage.formattingState.isBold = isBold;
    storage.formattingState.isItalics = isItalics;
    storage.formattingState.fontName = selectedFont.name;
  }, [editor, isBold, isItalics, selectedFont.name]);

  // Do operation (bold, italics, font change)
  useEffect(() => {
    if (!shouldDoOperation) {
      return;
    }

    const operations: TextEditorOperation[] = [];
    if (isBold) {
      operations.push('bold');
    }
    if (isItalics) {
      operations.push('italics');
    }

    applyFormattingToSelection(selectedFont.name, operations);
    setShouldDoOperation(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, isBold, isItalics, shouldDoOperation, selectedFont.name]);

  return (
    <div
      className={classNames(
        'dark:bg-base-100 relative flex items-center justify-between bg-white',
        className
      )}
      ref={containerRef}>
      <div className="flex items-center gap-2">
        {tools.includes('fonts') && (
          <Fragment>
            <FontSelectionDropdownButton
              className="z-20"
              selectedFont={selectedFont}
              onChange={onChangeFontOperation}
            />
          </Fragment>
        )}

        {tools.includes('fonts') && (tools.includes('b') || tools.includes('i')) && (
          <Fragment>
            <div className="ml-2" />
            <div className="text-gray-200 dark:text-gray-700">|</div>
            <div className="ml-2" />
          </Fragment>
        )}

        {/* Bold button */}
        {tools.includes('b') && (
          <SimpleButton
            buttonId="editor-toolbar-bold-button"
            className="z-20"
            isEnabled={isBoldButtonEnabled}
            onClick={onToggleBoldOperation}
            disabledMessage={boldSupportMessage}
            isActive={isBold}
            icon={<BoldIconSvg />}
            tooltipTitle="Bold"
            shortcutKey="B"
          />
        )}

        {/* Italics button */}
        {tools.includes('i') && (
          <SimpleButton
            buttonId="editor-toolbar-italics-button"
            className="z-20"
            isEnabled={isItalicsButtonEnabled}
            onClick={onToggleItalicsOperation}
            disabledMessage={italicsSupportMessage}
            isActive={isItalics}
            icon={<ItalicsIconSvg />}
            tooltipTitle="Italics"
            shortcutKey="I"
          />
        )}

        {(tools.includes('fonts') || tools.includes('b') || tools.includes('i')) &&
          (tools.includes('emoji') || tools.includes('attributes')) && (
            <Fragment>
              <div className="ml-2" />
              <div className="text-gray-200 dark:text-gray-700">|</div>
              <div className="mr-2" />
            </Fragment>
          )}

        {/* Attributes button */}
        {tools.includes('attributes') && (
          <Fragment>
            <AttributesDropdownButton
              isInlineToolbar={isInlineToolbar}
              className="z-20"
              editor={editor}
            />
            <FloatingAttributesDropdown
              editor={editor}
              isOpen={isAttributesFloatingDropdownOpen}
              ref={containerRef}
            />
          </Fragment>
        )}

        {/* Media button - Position icon button here */}
        {tools.includes('media') && (
          <InsertMediaDropdownButton
            editor={editor}
            className="z-20"
            isOpen={isMediaManagerDropdownOpen}
            jobId={jobId}
            onMediaSelected={onMediaSelected}
          />
        )}

        {/* Links button - Position icon button here */}
        {tools.includes('links') && jobType !== 'track-sent-links' && (
          <InsertLinkDropdownButton
            isInlineToolbar={isInlineToolbar}
            className="z-20"
            isIconButton={true}
            jobId={jobId}
          />
        )}

        {/* Emoji button */}
        {tools.includes('emoji') && (
          <Fragment>
            <EmojiPickerButton className="z-20" editor={editor} />
            <FloatingEmojiDropdown editor={editor} isOpen={isEmojiFloatingDropdownOpen} />
          </Fragment>
        )}

        {(tools.includes('fonts') ||
          tools.includes('b') ||
          tools.includes('i') ||
          tools.includes('emoji') ||
          tools.includes('attributes')) &&
          tools.includes('links') &&
          jobType === 'track-sent-links' && (
            <Fragment>
              <div className="ml-2" />
              <div className="text-gray-200 dark:text-gray-700">|</div>
              <div className="mr-2" />
            </Fragment>
          )}
        {/* Links button - Position full button here */}
        {tools.includes('links') && jobType === 'track-sent-links' && (
          <InsertLinkDropdownButton
            isInlineToolbar={isInlineToolbar}
            className="z-20"
            isIconButton={false}
            jobId={jobId}
          />
        )}
      </div>

      {/* Undo / Redo Buttons */}
      {tools.includes('undo') ||
        (tools.includes('redo') && (
          <div className="flex items-center gap-2">
            <UndoButton
              disabled={!canUndo}
              onClick={() => {
                if (editor) {
                  editor.chain().focus().undo().run();
                }
              }}
            />

            <RedoButton
              disabled={!canRedo}
              onClick={() => {
                if (editor) {
                  editor.chain().focus().redo().run();
                }
              }}
            />
          </div>
        ))}
    </div>
  );
};

export default TextEditorToolbar;
